import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import withStyles from '@mui/styles/withStyles';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import MuiDialogTitle from '@mui/material/DialogTitle';
import MuiDialogContent from '@mui/material/DialogContent';
import MuiDialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Typography from '@mui/material/Typography';
import HTMLFragment from 'components/HTMLFragment';
import { Check, Error, Info, Warning } from '@mui/icons-material';

const styles = theme => ({
  root: {
    margin: 0,
    padding: theme.spacing(2),
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.grey[500],
  },
});

const DialogTitle = withStyles(styles)(props => {
  const { children, classes, onClose, ...other } = props;
  return (
    <MuiDialogTitle
      color='white'
      disableTypography
      className={classes.root}
      {...other}
    >
      <Typography variant='h6'>{children}</Typography>
      {onClose ? (
        <IconButton
          aria-label='close'
          className={classes.closeButton}
          onClick={onClose}
          size='large'
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </MuiDialogTitle>
  );
});

const DialogContent = withStyles(theme => ({
  root: {
    padding: theme.spacing(2),
  },
}))(MuiDialogContent);

const DialogActions = withStyles(theme => ({
  root: {
    margin: 10,
    padding: theme.spacing(1),
  },
}))(MuiDialogActions);

const getTitle = type => {
  return {
    success: { title: 'Success', icon: <Check /> },
    error: { title: 'Error', icon: <Error /> },
    info: { title: 'Information', icon: <Info /> },
    warning: { title: 'Warning', icon: <Warning /> },
  }[type];
};

const StatusDialog = ({
  open,
  showClose = true,
  title,
  message,
  type,
  primaryAction = true,
  secondaryAction = true,
  primaryHandler,
  onCancel,
}) => {
  const handleClose = useCallback(() => {
    if (onCancel) {
      onCancel();
    }
  }, [onCancel]);
  const handlePrimary = useCallback(() => {
    if (primaryHandler) {
      primaryHandler();
    }
  }, [primaryHandler]);
  const handleSecondary = useCallback(() => {
    if (onCancel) {
      onCancel();
    }
  }, [onCancel]);
  const header = useMemo(() => {
    const obj = getTitle(type);
    if (title) {
      obj.title = title;
    }
    return obj;
  }, [title, type]);

  const primary = useMemo(() => {
    if (primaryAction === true) {
      return 'Ok';
    }
    return primaryAction;
  }, [primaryAction]);
  const secondary = useMemo(() => {
    if (secondaryAction === true) {
      return 'Cancel';
    }
    return secondaryAction;
  }, [secondaryAction]);

  return (
    <Dialog
      onClose={handleClose}
      aria-labelledby='status-dialog-title'
      open={open}
      disableEscapeKeyDown
    >
      <DialogTitle
        id='status-dialog-title'
        onClose={showClose && handleClose}
        style={{ color: header.color }}
      >
        {header.icon} {header.title}
      </DialogTitle>
      <DialogContent>
        <HTMLFragment>{message}</HTMLFragment>
      </DialogContent>
      <DialogActions>
        {secondary
          && (typeof secondary === 'object' ? (
            secondary
          ) : (
            <Button variant='contained' autoFocus onClick={handleSecondary}>
              {secondary}
            </Button>
          ))}
        {
          <Button
            autoFocus
            variant='contained'
            color='primary'
            onClick={handlePrimary}
            style={{}}
          >
            {primary}
          </Button>
        }
      </DialogActions>
    </Dialog>
  );
};

StatusDialog.propTypes = {
  open: PropTypes.bool,
  title: PropTypes.string,
  message: PropTypes.string,
  showClose: PropTypes.bool,
  type: PropTypes.oneOf(['success', 'error', 'info', 'warning']),
  primaryAction: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.string,
    PropTypes.element,
  ]),
  secondaryAction: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.string,
    PropTypes.element,
  ]),
  primaryHandler: PropTypes.func,
  onCancel: PropTypes.func,
};

export default StatusDialog;
